import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button/Button.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import AccountingDetailsFormSection from "~/components/formSections/AccountingDetailsFormSection";
import AddressFormSection from "~/components/formSections/AddressFormSection/AddressFormSection.tsx";
import CommentFormSection from "~/components/formSections/CommentFormSection";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { DEFAULT_COUNTRY_CODE } from "~/constants/form.ts";
import useCountryCodeSelectOptions from "~/hooks/form/formOptionsData/useCountryCodeSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	useClientValidationComparisonData,
} from "~/hooks/form/validationComparisonData/useClientValidationComparisonData.ts";
import { updateClient } from "~/modules/client/api/client/clientApiDispatchers.ts";
import type { Client, UpdateClientData } from "~/modules/client/api/client/clientTypes.ts";
import { getPaymentTargetDefaultValues, preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type Props = {
	onSuccess: () => void;
	onCancel: () => void;
	clientData: Client;
};

interface UpdateClientFormData extends Omit<UpdateClientData, "paymentTargetDays" | "address"> {
	addressLine1: string | null;
	addressLine2: string | null;
	addressLine3: string | null;
	city: string | null;
	postCode: string | null;
	countryCode: string | null;
	paymentTargetDays: number;
	paymentTargetDaysCustom?: number | null;
}

const UpdateClientForm: React.FC<Props> = ({ onSuccess, onCancel, clientData }) => {
	const [busy, setBusy] = useState(false);
	const displayNameComparisonData = useClientValidationComparisonData();

	const schema = useMemo(() => {
		return yup.object({
			addressLine1: yup.string().default(""),
			addressLine2: yup.string().default(""),
			addressLine3: yup.string().default(""),
			city: yup.string().default(""),
			comment: yup.string().default(""),
			countryCode: yup.string().default(""),
			displayName: yup.string().required().unique(displayNameComparisonData, clientData.id),
			invoiceByMail: yup.boolean().required(),
			isVatExempt: yup.boolean().required(),
			paymentTargetDays: yup.number().required(),
			paymentTargetDaysCustom: yup.number()
				.nullable()
				.when("paymentTargetDays", (field1Value: number[], schema) => {
					return field1Value[0] === 0
						? schema.required()
						: schema.nullable();
				}),
			postCode: yup.string().default(""),
			travelExpensesIncluded: yup.boolean().required(),
		});
	}, [displayNameComparisonData, clientData]);

	const existingAddress = clientData.address;

	const defaultValues = useMemo(() => {
		const { paymentTargetDays, paymentTargetDaysCustom } = getPaymentTargetDefaultValues(
			clientData.paymentTargetDays,
		);
		return {
			addressLine1: existingAddress?.addressLine1 || "",
			addressLine2: existingAddress?.addressLine2 || "",
			addressLine3: existingAddress?.addressLine3 || "",
			city: existingAddress?.city || "",
			comment: clientData.comment || "",
			countryCode: existingAddress?.countryCode || DEFAULT_COUNTRY_CODE,
			displayName: clientData.displayName,
			invoiceByMail: clientData.invoiceByMail,
			isVatExempt: clientData.isVatExempt,
			paymentTargetDays: paymentTargetDays,
			paymentTargetDaysCustom: paymentTargetDaysCustom,
			postCode: existingAddress?.postCode || "",
			travelExpensesIncluded: clientData.travelExpensesIncluded,
		};
	}, [clientData, existingAddress]);

	const {
		handleSubmit,
		control,
		watch,
		formState: { isValid, isDirty, isSubmitted },
		trigger,
	} = useForm<UpdateClientFormData>({
		mode: "onChange",
		defaultValues,
		resolver: yupResolver<UpdateClientFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isDirty,
		isValid,
		isSubmitted,
		isLoading: busy,
		enableInitially: false,
	});

	const showCustomPaymentTargetInput = watch("paymentTargetDays") === 0;

	const countryCodeSelectOptions = useCountryCodeSelectOptions();

	useEffect(() => {
		if (showCustomPaymentTargetInput) {
			trigger();
		}
	}, [showCustomPaymentTargetInput, trigger]);

	const onSubmit: SubmitHandler<UpdateClientFormData> = async (data) => {
		setBusy(true);
		const { paymentTargetDays, paymentTargetDaysCustom, addressLine1, addressLine2, addressLine3, city, postCode, countryCode, ...rest } = data;
		const paymentTargetDaysValue =
			paymentTargetDays === 0 && paymentTargetDaysCustom ? paymentTargetDaysCustom : paymentTargetDays;

		const hasAddress = addressLine1 || addressLine2 || addressLine3 || city || postCode || countryCode;
		const address = hasAddress ? { addressLine1, addressLine2, addressLine3, city, postCode, countryCode } : null;

		const updateData: UpdateClientData = { paymentTargetDays: paymentTargetDaysValue, address, ...rest };
		try {
			await updateClient(clientData.id, updateData);
			onSuccess();
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Firmendaten aktualisieren</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<FormSection title="Firma">
						<div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-6">
							<div className="col-span-full sm:col-start-1">
								<Input
									name="displayName"
									control={control}
									label="Name" />
							</div>
						</div>
					</FormSection>

					<AddressFormSection<UpdateClientFormData>
						control={control}
						countryCodeSelectOptions={countryCodeSelectOptions}
						initialShowLine3={!!existingAddress?.addressLine3}
					/>

					<AccountingDetailsFormSection<UpdateClientFormData>
						control={control}
						showCustomPaymentTargetInput={showCustomPaymentTargetInput}
					/>
					<CommentFormSection
						control={control}
						label="Rechnungsbesonderheiten"
						placeholder="Hier können allgemeine Anmerkungen für die Rechnungserstellung hinterlegt werden."
						title="Anmerkungen" />
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={!isValid}
								   className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					abbrechen
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default UpdateClientForm;
